import { getScrollTop } from './scroll-util';

class ScrollStick {
	constructor(el, topTriggerOffset = 0) {
		if (!el) {
			throw new Error('A valid element must be passed to ScrollStick');
		}

		this.stickEl = el;
		this.parentEl = this.stickEl.parentNode ?? null;
		this.enabled = false;

		this.currentScrollTop = getScrollTop();
		this.currentScrollBottom = this.currentScrollTop + window.innerHeight;

		this.stickElHeight = 0;
		this.parentElHeight = 0;
		this.parentElTopOffset = 0;
		this.stopStickPos = 0;

		this.stickElRect = this.stickEl.getBoundingClientRect();
		this.parentElRect = this.parentEl.getBoundingClientRect();

		this.curTopValue = parseInt(this.stickEl.style.position.top ?? 0, 10);
		this.topTriggerOffset = topTriggerOffset;

		this.refreshId = 0;
		this.hardRefreshId = 0;
		this.extraResizeId = 0;

		this.bodyObserver = new MutationObserver(() => {
			this.refresh(true);
		});

		this.enable();
	}

	resizeHandler = () => {
		if (!this.enabled) {
			return;
		}

		this.refresh(true);

		if (this.extraResizeId) {
			clearTimeout(this.extraResizeId);
		}

		this.extraResizeId = setTimeout(() => {
			this.refresh(true);
		}, 500);
	};

	scrollHandler = () => {
		if (!this.enabled) {
			return;
		}

		this.refresh();
	};

	setTopTriggerOffset(topTriggerOffset) {
		this.topTriggerOffset = topTriggerOffset;
		this.refresh(true);
	}

	addStickingClass(add) {
		if (add) {
			this.stickEl.classList.add('is-sticking');
		} else {
			this.stickEl.classList.remove('is-sticking');
		}
	}

	refresh(hardRefresh = false) {
		if (!this.enabled) {
			return;
		}

		if (hardRefresh && this.hardRefreshId) {
			cancelAnimationFrame(this.hardRefreshId);
		} else if (this.refreshId) {
			cancelAnimationFrame(this.refreshId);
		}

		const newRefreshId = requestAnimationFrame(() => {
			this.currentScrollTop = getScrollTop();
			this.currentScrollBottom = this.currentScrollTop + window.innerHeight;

			if (hardRefresh) {
				const bodyElRect = document.body.getBoundingClientRect();

				this.stickElRect = this.stickEl.getBoundingClientRect();
				this.parentElRect = this.parentEl.getBoundingClientRect();

				this.stickElHeight = this.stickElRect.height;
				this.parentElHeight = this.parentElRect.height;
				this.parentElTopOffset = Math.round(this.parentElRect.top - bodyElRect.top);
				this.stopStickPos =
					this.parentElTopOffset +
					this.parentElHeight -
					(this.stickElHeight + this.topTriggerOffset);
			}

			if (this.currentScrollTop <= this.parentElTopOffset - this.topTriggerOffset) {
				this.stickEl.style.setProperty('--sticky-top-offset', 0);

				this.addStickingClass(false);
			} else if (this.currentScrollTop < this.stopStickPos) {
				this.curTopValue =
					this.currentScrollTop - (this.parentElTopOffset - this.topTriggerOffset);

				this.stickEl.style.setProperty('--sticky-top-offset', `${this.curTopValue}px`);

				this.addStickingClass(true);
			} else {
				this.addStickingClass(false);
			}
		});

		if (hardRefresh) {
			this.hardRefreshId = newRefreshId;
		} else {
			this.refreshId = newRefreshId;
		}
	}

	enable() {
		if (this.enabled) {
			return;
		}

		this.enabled = true;
		this.refresh(true);

		window.addEventListener('resize', this.resizeHandler);
		document.addEventListener('scroll', this.scrollHandler);

		this.bodyObserver.observe(document.body, {
			childList: true,
			subtree: true
		});
	}

	disable() {
		if (!this.enabled) {
			return;
		}

		this.enabled = false;
		this.stickEl.style.setProperty('--sticky-top-offset', 0);

		window.removeEventListener('resize', this.resizeHandler);
		document.removeEventListener('scroll', this.scrollHandler);

		this.bodyObserver.disconnect();
	}
}

export default ScrollStick;
